<script setup lang="ts">
  import { VButton } from "@krainovsd/vue-ui";
  import dayjs from "dayjs";
  import { computed, ref } from "vue";
  import { useI18n } from "vue-i18n";
  import { PERIOD_OPTIONS, type PeriodType, getCustomPeriodRange } from "@/entities/report";
  import Sheet from "@/components/atoms/Sheet.vue";
  import Calendar, { type CalendarRange } from "@/components/molecules/Calendar.vue";
  import StepLayout from "@/components/molecules/constructor/StepLayout.vue";

  const { t } = useI18n();

  export type SelectedPeriod = PeriodType | "fixPeriod" | null;

  // Optional wording overrides so the step can be reused (e.g. the comparison
  // wizard); default to the report-wizard text.
  const props = defineProps<{ title?: string; subtitle?: string }>();

  const selectedKey = defineModel<SelectedPeriod>({ required: true });
  // Fixed-period bounds, kept as backend-ready UTC ISO strings.
  const dateFrom = defineModel<string | null>("dateFrom", { default: null });
  const dateTo = defineModel<string | null>("dateTo", { default: null });

  const emit = defineEmits<{ next: []; back: [] }>();

  const layoutRef = ref<{ rootEl: HTMLElement | null } | null>(null);
  defineExpose({
    rootEl: computed(() => layoutRef.value?.rootEl ?? null),
  });

  // Preset chips — every option except the custom range, which has its own chip.
  const periods = computed(() =>
    PERIOD_OPTIONS.filter((option) => option.key !== "fixPeriod").map((option) => ({
      ...option,
      label: t(option.label.path, {}, { default: option.label.default }),
    })),
  );

  // "Свой период" label, sourced from PERIOD_OPTIONS.
  const fixPeriodLabel = computed(() => {
    const option = PERIOD_OPTIONS.find((item) => item.key === "fixPeriod");
    return option ? t(option.label.path, {}, { default: option.label.default }) : "";
  });

  const sheetOpen = ref(false);
  // `draft` is what the user is editing in the sheet; `committed` is what was
  // actually applied (kept as local Dates so the chip label has no timezone drift).
  const draftRange = ref<CalendarRange>({ start: null, end: null });
  const committedRange = ref<CalendarRange>({ start: null, end: null });

  const canApply = computed(() => draftRange.value.start !== null && draftRange.value.end !== null);

  const fixedPeriodLabel = computed(() => {
    const { start, end } = committedRange.value;
    if (selectedKey.value !== "fixPeriod" || !start || !end) {
      return fixPeriodLabel.value;
    }
    return `${dayjs(start).format("DD.MM.YYYY")} — ${dayjs(end).format("DD.MM.YYYY")}`;
  });

  // Valid when a preset is chosen; a fixed period also needs both dates.
  const valid = computed(() => {
    if (selectedKey.value === null) return false;
    if (selectedKey.value === "fixPeriod") return Boolean(dateFrom.value && dateTo.value);
    return true;
  });

  function selectPreset(key: SelectedPeriod) {
    selectedKey.value = key;
    // A dynamic preset supersedes any fixed range.
    committedRange.value = { start: null, end: null };
    dateFrom.value = null;
    dateTo.value = null;
  }

  function openFixedPeriod() {
    // Start editing from the currently applied range.
    draftRange.value = { ...committedRange.value };
    sheetOpen.value = true;
  }

  function applyFixedPeriod() {
    const { start, end } = draftRange.value;
    if (!start || !end) return;
    const period = getCustomPeriodRange(start, end);
    dateFrom.value = period.dateFrom;
    dateTo.value = period.dateTo;
    committedRange.value = { start, end };
    selectedKey.value = "fixPeriod";
    sheetOpen.value = false;
  }
</script>

<template>
  <StepLayout
    ref="layoutRef"
    :title="props.title ?? $t('page.createReport.step3.title', {}, { default: 'Выберите период' })"
    :subtitle="
      props.subtitle ??
      $t(
        'page.createReport.step3.subtitle',
        {},
        { default: 'Динамический пресет или фиксированные даты' },
      )
    "
    back
    next
    :disabled="!valid"
    @back="emit('back')"
    @next="emit('next')"
  >
    <div :class="$style.periodChips">
      <div
        v-for="option in periods"
        :key="option.key"
        :class="[$style.periodChip, selectedKey === option.key && $style.periodChipActive]"
        @click="selectPreset(option.key as SelectedPeriod)"
      >
        {{ option.label }}
      </div>
    </div>
    <div
      :class="[$style.periodChip, selectedKey === 'fixPeriod' && $style.periodChipActive]"
      @click="openFixedPeriod"
    >
      {{ fixedPeriodLabel }}
    </div>
  </StepLayout>

  <Sheet :open="sheetOpen" :title="fixPeriodLabel" @update:open="sheetOpen = $event">
    <div :class="$style.fixedPeriod">
      <Calendar v-model:range="draftRange" mode="range" locale="ru-RU" />
      <div :class="$style.fixedPeriodFooter">
        <VButton type="primary" :disabled="!canApply" @click="applyFixedPeriod">
          {{ $t("page.createReport.step3.apply", {}, { default: "Применить" }) }}
        </VButton>
      </div>
    </div>
  </Sheet>
</template>

<style lang="scss" module>
  .periodChips {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .periodChip {
    padding: 0.625rem 1rem;

    display: flex;
    align-items: center;

    border: 1px solid var(--border);
    border-radius: 0.875rem;

    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.25rem;

    color: var(--foreground);

    cursor: pointer;
  }

  .periodChipActive {
    border-color: var(--brand);
  }

  .fixedPeriod {
    width: 100%;
    max-width: 20rem;
    margin: 0 auto;

    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .fixedPeriodFooter {
    display: flex;
    justify-content: flex-end;
  }
</style>
